import { RoomState } from '../models/RoomState'
import { type SlideDeckRole } from '../models/SlideDeckRole'
import { type RoleAssignmentIssue } from '../types'

/**
 * Validates a room state against role assignment requirements and returns any issues found.
 * Checks for user count violations, unassigned users, and role distribution problems.
 * Returns null if all role assignment constraints are satisfied.
 */
export function getRoleIssueForRoomState({
  roles,
  roomState,
}: {
  roles: SlideDeckRole[]
  roomState: RoomState
}): { issue: RoleAssignmentIssue; min: number; max: number } | null {
  if (!roles.length) return null

  if (!roomState.users.length) return null

  const { min, max } = roles.reduce(
    (acc, role) => {
      acc.min += role.data.roleMin
      acc.max += role.data.roleMax
      acc.max = Math.min(acc.max, RoomState.maxAllowedUsers)
      return acc
    },
    { min: 0, max: 0 }
  )

  if (roomState.userCount < min) return { issue: 'belowMinUsers', min, max }
  if (roomState.userCount > max) return { issue: 'aboveMaxUsers', min, max }

  const anyUnassigned = roomState.users.some(
    (user) => !(user.id in (roomState.data.assignmentRoles ?? {}))
  )

  if (anyUnassigned) return { issue: 'unassignedUsers', min, max }

  const anyRolesInvalid = (() => {
    const roleCounts: { [roleId: string]: number } = {}
    roomState.users.forEach((user) => {
      const roleId = roomState.data.assignmentRoles?.[user.id]
      if (roleId) {
        roleCounts[roleId] = (roleCounts[roleId] || 0) + 1
      }
    })

    return roles.some((role) => {
      const count = roleCounts[role.id] || 0
      return count < role.data.roleMin || count > role.data.roleMax
    })
  })()

  if (anyRolesInvalid) return { issue: 'invalidRoles', min, max }

  return null
}
